import { fetch, HTTPError } from '@ixigo/shell/fetch';

// The creators backend (creator-platform). It's called by relative path, like
// the rest of the app's APIs: deployed, the host serving these pages routes
// /creator-platform to it, and in dev the Vite fallback proxy forwards it to
// creator-build (see vite.config.ts). The app's fetch hooks add the client
// headers and the login's Bearer token.
const CREATORS_API = 'creator-platform/api/v1/creators';

/** A creators API call the backend refused. `message` is written for the
 *  creator, so it can be shown as it is. */
export class CreatorsApiError extends Error {
	readonly status: number;
	/** BELOW_MINIMUM, BALANCE_CHANGED, CREATOR_NOT_APPROVED, …; null when the
	 *  response had no error body. */
	readonly code: string | null;

	constructor(status: number, code: string | null, message: string) {
		super(message);
		this.name = 'CreatorsApiError';
		this.status = status;
		this.code = code;
	}
}

/** The backend's error body: `{ code, error_code, message }`. */
interface ErrorBody {
	error_code?: string;
	message?: string;
}

// HTTP errors become CreatorsApiErrors; anything else (offline, timeouts) is
// rethrown as it is.
async function toCreatorsApiError(error: unknown): Promise<unknown> {
	if (!(error instanceof HTTPError)) return error;
	const body: ErrorBody = await error.response.json().catch(() => ({}));
	return new CreatorsApiError(
		error.response.status,
		body.error_code ?? null,
		body.message ?? error.message,
	);
}

export async function getCreators<T>(
	path: string,
	searchParams?: Record<string, string | number>,
): Promise<T> {
	try {
		return await fetch
			.get(`${CREATORS_API}/${path}`, { searchParams })
			.json<T>();
	} catch (error) {
		throw await toCreatorsApiError(error);
	}
}

export async function postCreators<T>(path: string, json: unknown): Promise<T> {
	try {
		return await fetch.post(`${CREATORS_API}/${path}`, { json }).json<T>();
	} catch (error) {
		throw await toCreatorsApiError(error);
	}
}
